import { z } from 'zod';
import { dateOnlySchema } from './patients.js';

/**
 * Export comptable : ce que le cabinet remet à son comptable pour une période.
 *
 * **Le choix du format, et pourquoi il est neutre.** La V1 exportait au format BOB50 — un format
 * propriétaire du logiciel belge du même nom — et seulement côté PhysioPro. Reprendre BOB50 ici
 * reviendrait à choisir le logiciel du comptable à sa place, avant même de savoir lequel il
 * utilise : au Luxembourg on rencontre Sage BOB, Winbooks, Odoo et quelques tableurs. Tous savent
 * importer un fichier délimité en donnant la correspondance de ses colonnes.
 *
 * On produit donc un **CSV documenté et stable** : point-virgule en séparateur et virgule décimale,
 * comme l'attend un tableur configuré en français ou en allemand ; dates en `YYYY-MM-DD`, qui ne
 * s'inversent pas d'un pays à l'autre ; en-tête explicite ; une ligne par pièce. Le jour où un
 * format propriétaire sera tranché, il se construira par-dessus la même lecture — c'est une
 * question de mise en forme, pas de données.
 *
 * **Ce qu'il contient et ce qu'il ne contient pas.** Des pièces comptables : numéro, dates,
 * montants, moyen d'encaissement, et l'identité du débiteur, sans laquelle un journal de ventes ne
 * s'impute pas. Le journal des ventes porte une ligne par facture émise et une ligne par avoir
 * émis, chacun à sa date : un avoir de mai ne réécrit pas la facture de mars. **Aucune donnée
 * clinique** n'en approche : ni motif, ni consultation, ni document.
 */

export const accountingExportKinds = ['invoices', 'payments', 'interests'] as const;
export type AccountingExportKind = (typeof accountingExportKinds)[number];

export const accountingExportQuerySchema = z
  .object({
    /**
     * Bornes incluses : date d'émission des factures, d'encaissement des règlements, ou de la
     * relance qui a fixé les intérêts.
     */
    from: dateOnlySchema,
    to: dateOnlySchema,
    kind: z.enum(accountingExportKinds).default('invoices'),
  })
  .strict()
  .refine((value) => value.to >= value.from, 'La fin de période précède son début');
export type AccountingExportQuery = z.infer<typeof accountingExportQuerySchema>;

/**
 * Colonnes du journal des **ventes**. L'ordre est celui du fichier : il fait partie du contrat, un
 * comptable ayant réglé sa correspondance une fois ne doit pas la refaire au trimestre suivant.
 */
export const accountingInvoiceColumns = [
  'numero',
  'date_emission',
  'date_echeance',
  'statut',
  'serie',
  'regime_tva',
  'montant_ht',
  'montant_tva',
  'montant_ttc',
  'montant_encaisse',
  'montant_avoir',
  'reste_du',
  'client',
  'client_societe',
  'client_tva',
  'praticien',
] as const;

/**
 * Colonnes du journal des **encaissements**. `montant` est ce qui a été reçu ; `dont_interets` en
 * est la part imputée aux intérêts de retard, ajoutée **en fin de ligne** le 25 septembre 2026 pour
 * qu'une correspondance déjà réglée sur les huit premières colonnes reste juste.
 */
export const accountingPaymentColumns = [
  'date_encaissement',
  'facture',
  'moyen',
  'montant',
  'statut',
  'prestataire',
  'reference_prestataire',
  'client',
  'dont_interets',
] as const;

/**
 * Colonnes du journal des **intérêts de retard** : une ligne par créance fixée par une relance
 * envoyée dans la période. Des montants seulement : le compte et le traitement de TVA de ces
 * sommes sont l'affaire du comptable, aucun n'est supposé ici.
 */
export const accountingInterestColumns = [
  'date_relance',
  'facture',
  'niveau_relance',
  'montant',
  'montant_encaisse',
  'montant_abandonne',
  'reste_du',
  'statut',
  'date_abandon',
  'client',
] as const;
